import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { requestDataExport, requestAccountDeletion, getConsentStatus, updateConsent } from "../lib/api-client";

function PrivacyPage(): JSX.Element {
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const consentQuery = useQuery({
    queryKey: ["consent-status"],
    queryFn: getConsentStatus,
  });

  // Update consent state when query succeeds
  if (consentQuery.data && !analyticsConsent && !marketingConsent) {
    setAnalyticsConsent(consentQuery.data.consent.analytics_consent);
    setMarketingConsent(consentQuery.data.consent.marketing_consent);
  }

  const exportMutation = useMutation({
    mutationFn: requestDataExport,
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rwaforms-data-export-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => requestAccountDeletion("DELETE_MY_DATA"),
    onSuccess: () => {
      // Redirect to login after deletion
      window.location.href = "/login";
    },
  });

  const consentMutation = useMutation({
    mutationFn: () => updateConsent(analyticsConsent, marketingConsent),
    onSuccess: () => {
      void consentQuery.refetch();
    },
  });

  const handleDeleteClick = () => {
    if (deleteConfirmation === "DELETE_MY_DATA") {
      void deleteMutation.mutateAsync();
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          padding: "1.5rem 2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
        }}
      >
        <div>
          <h1 style={{ fontSize: "1.5rem", margin: 0 }}>Privacy & GDPR</h1>
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            Manage your data and privacy preferences.
          </p>
        </div>
      </header>

      <main style={{ padding: "2rem", display: "grid", gap: "2rem", flex: 1, maxWidth: "600px" }}>
        <section className="surface" style={{ padding: "1.5rem" }}>
          <header style={{ marginBottom: "1rem" }}>
            <h2 style={{ marginBottom: "0.3rem" }}>Data Export (GDPR Article 15)</h2>
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Download a copy of all your personal data in JSON format.
            </p>
          </header>

          <p style={{ color: "var(--color-text-muted)", fontSize: "0.875rem", marginBottom: "1rem" }}>
            This includes your profile, teams, forms, responses, and activity logs.
          </p>

          <button
            type="button"
            className="button secondary"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            {exportMutation.isPending ? "Preparing export..." : "Download my data"}
          </button>

          {exportMutation.isError && (
            <div
              style={{
                marginTop: "1rem",
                padding: "0.75rem",
                borderRadius: "var(--radius-base)",
                background: "var(--color-critical-soft)",
                color: "var(--color-critical)",
                fontSize: "0.875rem",
              }}
            >
              Failed to export data. Please try again.
            </div>
          )}
        </section>

        <section className="surface" style={{ padding: "1.5rem" }}>
          <header style={{ marginBottom: "1rem" }}>
            <h2 style={{ marginBottom: "0.3rem" }}>Data Processing Consent</h2>
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Manage your consent for data processing.
            </p>
          </header>

          {consentQuery.isLoading ? (
            <div style={{ color: "var(--color-text-muted)" }}>Loading preferences...</div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void consentMutation.mutateAsync();
              }}
              style={{ display: "grid", gap: "1rem" }}
            >
              <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={analyticsConsent}
                  onChange={(e) => setAnalyticsConsent(e.target.checked)}
                />
                <span>
                  <div style={{ fontWeight: 500 }}>Analytics</div>
                  <div style={{ fontSize: "0.875rem", color: "var(--color-text-muted)" }}>
                    Allow us to collect usage analytics to improve the platform
                  </div>
                </span>
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                />
                <span>
                  <div style={{ fontWeight: 500 }}>Marketing</div>
                  <div style={{ fontSize: "0.875rem", color: "var(--color-text-muted)" }}>
                    Allow us to send you product updates and promotional emails
                  </div>
                </span>
              </label>

              <button type="submit" className="button primary" disabled={consentMutation.isPending}>
                {consentMutation.isPending ? "Saving..." : "Save preferences"}
              </button>
            </form>
          )}
        </section>

        <section className="surface" style={{ padding: "1.5rem", borderColor: "var(--color-critical-soft)" }}>
          <header style={{ marginBottom: "1rem" }}>
            <h2 style={{ marginBottom: "0.3rem", color: "var(--color-critical)" }}>
              Delete Account (GDPR Article 17)
            </h2>
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Permanently delete your account and all associated data.
            </p>
          </header>

          <p style={{ color: "var(--color-text-muted)", fontSize: "0.875rem", marginBottom: "1rem" }}>
            This action is irreversible. All your data will be permanently deleted from our systems.
          </p>

          {!showDeleteConfirm ? (
            <button
              type="button"
              className="button"
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                background: "var(--color-critical-soft)",
                color: "var(--color-critical)",
                border: "1px solid var(--color-critical)",
              }}
            >
              Delete my account
            </button>
          ) : (
            <div style={{ display: "grid", gap: "1rem" }}>
              <div
                style={{
                  padding: "1rem",
                  borderRadius: "var(--radius-base)",
                  background: "var(--color-critical-soft)",
                  color: "var(--color-critical)",
                  fontSize: "0.875rem",
                }}
              >
                <strong>Warning:</strong> This will permanently delete your account and all data. This cannot be undone.
              </div>

              <label className="field">
                <span>Type "DELETE_MY_DATA" to confirm</span>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="DELETE_MY_DATA"
                />
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                <button
                  type="button"
                  className="button ghost"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmation("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={handleDeleteClick}
                  disabled={deleteConfirmation !== "DELETE_MY_DATA" || deleteMutation.isPending}
                  style={{
                    background: "var(--color-critical)",
                    color: "white",
                  }}
                >
                  {deleteMutation.isPending ? "Deleting..." : "Delete account"}
                </button>
              </div>

              {deleteMutation.isError && (
                <div
                  style={{
                    padding: "0.75rem",
                    borderRadius: "var(--radius-base)",
                    background: "var(--color-critical-soft)",
                    color: "var(--color-critical)",
                    fontSize: "0.875rem",
                  }}
                >
                  Failed to delete account. Please try again.
                </div>
              )}
            </div>
          )}
        </section>

        <section className="surface" style={{ padding: "1.5rem" }}>
          <header style={{ marginBottom: "1rem" }}>
            <h2 style={{ marginBottom: "0.3rem" }}>Privacy Policy</h2>
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Learn how we handle your data.
            </p>
          </header>

          <div style={{ fontSize: "0.875rem", lineHeight: 1.6, color: "var(--color-text-muted)" }}>
            <p>
              We are committed to protecting your privacy. Our privacy policy outlines how we collect, use, and protect
              your personal data in accordance with GDPR and other applicable regulations.
            </p>
            <p>
              For more information, please visit our{" "}
              <a href="/privacy-policy" style={{ color: "var(--color-primary)" }}>
                full privacy policy
              </a>
              .
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}

export default PrivacyPage;
